/**
 * ApiKeysPageV2 — redesigned API Keys list.
 *
 * Layout: PageHeader + LibTable. Columns:
 *   Name · Owner · Scopes (mono pills or "All" chip) · Expires
 *   (date or red "Expired" pill) · Last used · Actions
 *
 * Description hangs below the name as a muted sub-line — keeps the
 * scopes column wide instead of letting prose squeeze it.
 *
 * Data flow: same `useApiKeys()` hook the v1 page uses.
 */
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useApiKeys } from '../../../../hooks/useApiKeys';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import {
  PageHeader,
  Button,
  LibTable,
  StatusPill,
  IconButton,
  cn,
} from '../../../redesign';

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

const TrashIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

export function formatDate(iso) {
  if (!iso) return 'Never';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function ScopePill({ children, tone = 'neutral' }) {
  const palette = {
    // Theme-aware so scope pills flip in dark mode. Hardcoded light
    // cream/navy left them as light pills on the dark canvas.
    neutral: {
      bg: 'rgb(var(--color-cream))',
      color: 'rgb(var(--color-navy))',
      border: 'rgb(var(--color-sand))',
    },
    // The "All" pill — tokenized so it flips, but kept on the
    // cyan/teal-bright hue (#1AA6B7 light / #2DBED4 dark) to preserve
    // the original #1A8A98 cyan look. teal-green would have shifted it
    // to sage green — a visible hue change (Bugbot caught this).
    info: {
      // Alpha matches the original 8-digit hex: 0x18≈0.09 bg, 0x33≈0.2
      // border (not 0.12 / 0.33 — those mis-read the hex as decimal).
      bg: 'rgb(var(--color-teal-bright) / 0.09)',
      color: 'rgb(var(--color-teal-bright))',
      border: 'rgb(var(--color-teal-bright) / 0.2)',
    },
  }[tone] || {};
  return (
    <span
      className="font-mono"
      style={{
        fontSize: 10.5,
        padding: '2px 7px',
        borderRadius: 999,
        background: palette.bg,
        color: palette.color,
        border: `1px solid ${palette.border}`,
      }}
    >
      {children}
    </span>
  );
}

export default function ApiKeysPageV2() {
  const navigate = useNavigate();
  const { apiKeys, loading, fetchAll, revoke } = useApiKeys();

  useEffect(() => {
    fetchAll();
    // fetchAll is a useCallback from the hook; safe to omit.
  }, [fetchAll]);

  const handleRevoke = (id, name) => {
    Swal.fire({
      title: 'Revoke API Key?',
      text: `"${name}" will be permanently revoked. Any integrations using this key will stop working.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, revoke it!',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.value) revoke(id);
    });
  };

  const total = Array.isArray(apiKeys) ? apiKeys.length : undefined;
  const totalLabel = total != null ? `${total.toLocaleString()} total` : undefined;

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={!!loading} />
      <PageHeader
        eyebrow="Settings & Admin"
        title="API Keys"
        total={totalLabel}
        actions={
          <Link to="/api-keys/create" className="no-underline">
            <Button variant="coral" leadingIcon={PlusSign}>
              Create new key
            </Button>
          </Link>
        }
      />

      <div style={{ padding: '0 28px 32px' }}>
        {!apiKeys || apiKeys.length === 0 ? (
          <div
            className="bg-surface border border-sand text-text-muted italic text-center"
            style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
          >
            {loading
              ? 'Loading API keys…'
              : 'No API keys yet — create one to get started.'}
          </div>
        ) : (
          <LibTable
            fixed
            className="border border-sand"
            style={{ borderRadius: 12, overflow: 'hidden' }}
          >
            <colgroup>
              {/* name + desc */}
              <col />
              {/* owner */}
              <col style={{ width: '14%' }} />
              {/* scopes */}
              <col style={{ width: '20%' }} />
              {/* Expires + Last used hold "Nov 13, 2025, 03:45 PM"
               *  (~22 chars at 11.5px mono ≈ 170px), so the dates
               *  need a bit more room than the previous 140/160. */}
              {/* expires */}
              <col style={{ width: 180 }} />
              {/* last used */}
              <col style={{ width: 200 }} />
              {/* actions */}
              <col style={{ width: 100 }} />
            </colgroup>
            <LibTable.Head>
              <tr>
                <LibTable.Th>Name &amp; description</LibTable.Th>
                <LibTable.Th>Owner</LibTable.Th>
                <LibTable.Th>Scopes</LibTable.Th>
                <LibTable.Th>Expires</LibTable.Th>
                <LibTable.Th>Last used</LibTable.Th>
                <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
              </tr>
            </LibTable.Head>
            <tbody>
              {apiKeys.map((t) => {
                const scopes = Array.isArray(t.abilities) ? t.abilities : [];
                const isWildcard = scopes.length === 0 || (scopes.length === 1 && scopes[0] === '*');
                const expired = !!t.is_expired;
                return (
                  <LibTable.Row key={t.id} inactive={expired}>
                    <LibTable.Td>
                      <Link
                        to={`/api-keys/${t.id}/edit`}
                        className="font-semibold text-navy no-underline hover:text-teal-green-dark block truncate"
                        style={{ fontSize: 13.5, lineHeight: 1.25 }}
                        title={t.name || ''}
                      >
                        {t.name || 'Untitled'}
                      </Link>
                      {t.description && (
                        <div
                          className="text-text-muted truncate"
                          style={{ fontSize: 11.5, marginTop: 2 }}
                          title={t.description}
                        >
                          {t.description}
                        </div>
                      )}
                    </LibTable.Td>
                    <LibTable.Td>
                      <span className="text-navy truncate" style={{ fontSize: 12.5 }}>
                        {t.tokenable?.admin_name || '—'}
                      </span>
                    </LibTable.Td>
                    <LibTable.Td>
                      <div
                        className="flex flex-wrap"
                        style={{ gap: 4 }}
                      >
                        {isWildcard ? (
                          <ScopePill tone="info">All</ScopePill>
                        ) : (
                          scopes.map((s, i) => (
                            <ScopePill key={i}>{s}</ScopePill>
                          ))
                        )}
                      </div>
                    </LibTable.Td>
                    <LibTable.Td>
                      {expired ? (
                        <StatusPill tone="coral">Expired</StatusPill>
                      ) : (
                        <span
                          className="font-mono text-text-muted"
                          style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}
                        >
                          {t.expires_at ? formatDate(t.expires_at) : 'Never'}
                        </span>
                      )}
                    </LibTable.Td>
                    <LibTable.Td>
                      <span
                        className="font-mono text-text-muted"
                        style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}
                      >
                        {formatDate(t.last_used_at)}
                      </span>
                    </LibTable.Td>
                    <LibTable.Td style={{ textAlign: 'right' }}>
                      <div
                        className={cn(
                          'inline-flex opacity-0 transition-opacity duration-150',
                          'group-hover:opacity-100',
                        )}
                        style={{ gap: 4 }}
                      >
                        <IconButton
                          size="sm"
                          aria-label="Edit"
                          title="Edit"
                          onClick={() => navigate(`/api-keys/${t.id}/edit`)}
                        >
                          {EditIconSvg}
                        </IconButton>
                        <IconButton
                          size="sm"
                          aria-label="Revoke"
                          title="Revoke"
                          onClick={() => handleRevoke(t.id, t.name)}
                        >
                          {TrashIconSvg}
                        </IconButton>
                      </div>
                    </LibTable.Td>
                  </LibTable.Row>
                );
              })}
            </tbody>
          </LibTable>
        )}
      </div>
    </div>
  );
}
